import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft, Send, Star } from 'lucide-react';
import { toast } from 'sonner';
import DocumentUploader from '../components/DocumentUploader';
import { StarRating } from '../components/FeedbackWidget';
import TenderLifecycle from '../components/TenderLifecycle';
import PolicyComplianceBadge from '../components/PolicyComplianceBadge';
import SupplierAwardWorkflow from '../components/SupplierAwardWorkflow';

const TenderDetails = () => {
  const { tenderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tender, setTender] = useState(null);
  const [bids, setBids] = useState([]);
  const [bidForm, setBidForm] = useState({ quoted_amount: '', comments: '', attachments: [] });
  const [loading, setLoading] = useState(true);
  const [submittingBid, setSubmittingBid] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({ rating: 0, comment: '' });
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [supplierProfile, setSupplierProfile] = useState(null);

  const submitAwardFeedback = async (e) => {
    e.preventDefault();
    if (!feedbackForm.rating) return toast.error('Pick a star rating');
    try {
      await axios.post(`${API}/feedback/award`, { tender_id: tenderId, rating: feedbackForm.rating, comment: feedbackForm.comment }, { withCredentials: true });
      toast.success('Feedback submitted. Thank you!');
      setFeedbackSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    }
  };

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchTender();
    if (user?.role === 'buyer') {
      fetchBids();
    }
    if (user?.role === 'supplier') {
      axios.get(`${API}/suppliers/me`, { withCredentials: true }).then(r => setSupplierProfile(r.data)).catch(() => {});
    }
  }, [tenderId]);

  const fetchTender = async () => {
    try {
      const { data } = await axios.get(`${API}/tenders/${tenderId}`, { withCredentials: true });
      setTender(data);
    } catch (error) {
      toast.error('Failed to fetch tender details');
    } finally {
      setLoading(false);
    }
  };

  const fetchBids = async () => {
    try {
      const { data } = await axios.get(`${API}/bids/tender/${tenderId}`, { withCredentials: true });
      setBids(data);
    } catch (error) {
      console.error('Failed to fetch bids');
    }
  };

  const handleSubmitBid = async (e) => {
    e.preventDefault();
    setSubmittingBid(true);
    try {
      await axios.post(
        `${API}/bids`,
        {
          tender_id: tenderId,
          quoted_amount: parseFloat(bidForm.quoted_amount),
          comments: bidForm.comments,
          attachments: bidForm.attachments,
        },
        { withCredentials: true }
      );
      toast.success('Bid submitted successfully!');
      setBidForm({ quoted_amount: '', comments: '', attachments: [] });
      navigate('/supplier-dashboard');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to submit bid';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to submit bid');
    } finally {
      setSubmittingBid(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      await axios.patch(`${API}/tenders/${tenderId}`, { status: newStatus }, { withCredentials: true });
      toast.success('Tender status updated!');
      fetchTender();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!tender) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-muted-foreground">Tender not found</p>
          <button onClick={() => navigate(-1)} className="btn-primary mt-4">Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass border-b border-border/40 sticky top-0 z-50">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground" data-testid="back-btn">
              <ArrowLeft className="h-6 w-6" />
            </button>
            <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'hsl(var(--primary))' }}>Tender Details</h1>
          </div>
        </nav>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 max-w-5xl">
        {/* Tender Info */}
        <div className="bg-card border border-border rounded-lg p-8 mb-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-3xl font-bold mb-2">{tender.title}</h2>
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <span>Category: {tender.work_category}</span>
                <span>•</span>
                <span>Location: {tender.location}</span>
              </div>
            </div>
            <span className={`status-badge status-${tender.status}`}>{tender.status.replace(/_/g, ' ').toUpperCase()}</span>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <div className="text-sm text-muted-foreground mb-1">Estimated Procurement Budget</div>
              <div className="text-2xl font-semibold">
                {tender.estimated_value != null
                  ? `₹${Number(tender.estimated_value).toLocaleString('en-IN')}`
                  : <span className="text-base italic text-muted-foreground">Not Available</span>}
              </div>
            </div>
            {tender.emd_amount && (
              <div>
                <div className="text-sm text-muted-foreground mb-1">EMD Amount</div>
                <div className="text-2xl font-semibold">₹{tender.emd_amount.toLocaleString()}</div>
              </div>
            )}
            <div>
              <div className="text-sm text-muted-foreground mb-1">Start Date</div>
              <div className="text-lg">{new Date(tender.start_date).toLocaleDateString()}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-1">End Date</div>
              <div className="text-lg">{new Date(tender.end_date).toLocaleDateString()}</div>
            </div>
          </div>

          {tender.scope_of_work && (
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2">Scope of Work</h3>
              <p className="text-muted-foreground whitespace-pre-wrap">{tender.scope_of_work}</p>
            </div>
          )}

          {tender.eligibility_criteria && (
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2">Eligibility Criteria</h3>
              <p className="text-muted-foreground whitespace-pre-wrap">{tender.eligibility_criteria}</p>
            </div>
          )}

          {tender.required_documents && (
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2">Required Documents</h3>
              <p className="text-muted-foreground whitespace-pre-wrap">{tender.required_documents}</p>
            </div>
          )}

          {tender.site_visit_details && (
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2">Site Visit Details</h3>
              <p className="text-muted-foreground whitespace-pre-wrap">{tender.site_visit_details}</p>
            </div>
          )}

          {/* (Buyer action buttons are now driven by TenderLifecycle below) */}
        </div>

        {/* 12-Stage Procurement Lifecycle */}
        <div className="mb-8" data-testid="tender-lifecycle-section">
          <PolicyComplianceBadge tenderId={tenderId} />
          <TenderLifecycle tenderId={tenderId} role={user?.role} onChanged={fetchTender} />
        </div>

        {/* Supplier Bid Form */}
        {user?.role === 'supplier' && tender.status === 'published' && (
          <div className="bg-card border border-border rounded-lg p-6 mb-8">
            <h3 className="text-xl font-semibold mb-4">Submit Your Bid</h3>
            <form onSubmit={handleSubmitBid} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Quoted Amount (₹) *</label>
                <input
                  type="number"
                  value={bidForm.quoted_amount}
                  onChange={(e) => setBidForm({ ...bidForm, quoted_amount: e.target.value })}
                  required
                  min="0"
                  className="w-full px-4 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-ring"
                  data-testid="bid-amount-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Comments</label>
                <textarea
                  value={bidForm.comments}
                  onChange={(e) => setBidForm({ ...bidForm, comments: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-ring"
                  data-testid="bid-comments-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Bid Attachments (Optional)</label>
                <DocumentUploader
                  files={bidForm.attachments}
                  onChange={(attachments) => setBidForm({ ...bidForm, attachments })}
                  purpose="bid_attachment"
                  maxFiles={5}
                />
              </div>
              <button type="submit" disabled={submittingBid} className="btn-primary" data-testid="submit-bid-btn">
                <Send className="h-4 w-4 mr-2" />
                {submittingBid ? 'Submitting...' : 'Submit Bid'}
              </button>
            </form>
          </div>
        )}

        {/* Bids Table (Buyer Only) */}
        {user?.role === 'buyer' && bids.length > 0 && (
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Received Bids</h3>
              <a href={`/buyer/negotiation/tender/${tender?.id}`} className="text-sm px-3 py-1.5 rounded bg-navy text-white font-semibold hover:opacity-90" data-testid="open-negotiation-btn">
                Open Negotiation Dashboard →
              </a>
            </div><div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-medium">Supplier</th>
                    <th className="text-left py-3 px-4 font-medium">Quoted Amount</th>
                    <th className="text-left py-3 px-4 font-medium">Comments</th>
                    <th className="text-left py-3 px-4 font-medium">Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {bids.map((bid) => (
                    <tr key={bid.id} className="border-b border-border hover:bg-muted/50" data-testid={`bid-row-${bid.id}`}>
                      <td className="py-3 px-4">{bid.supplier?.company_name}</td>
                      <td className="py-3 px-4">₹{bid.quoted_amount.toLocaleString()}</td>
                      <td className="py-3 px-4">{bid.comments || '-'}</td>
                      <td className="py-3 px-4">{new Date(bid.submitted_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SUPPLIER V1 · Phase B — Module 9 + 10 + 11 (awarded supplier only) */}
        {tender && user?.role === 'supplier' && tender.awarded_supplier_id && supplierProfile && tender.awarded_supplier_id === supplierProfile.id && (
          <SupplierAwardWorkflow tender={tender} refresh={fetchTender} />
        )}

        {/* Post-Award Feedback */}
        {tender && (tender.status === 'awarded' || tender.status === 'closed') && (user?.role === 'buyer' || user?.role === 'supplier') && (
          <div className="bg-gold/5 border-2 border-gold/30 rounded-lg p-6 mt-6" data-testid="award-feedback-card">
            <div className="flex items-center mb-3">
              <Star className="h-5 w-5 text-orange mr-2" />
              <h3 className="text-lg font-bold text-navy">
                {user?.role === 'buyer' ? 'Rate the Awarded Supplier' : 'Rate the Buyer'}
              </h3>
            </div>
            {feedbackSubmitted ? (
              <p className="text-sm text-green-700 font-semibold">✓ Thank you — your feedback has been recorded.</p>
            ) : (
              <form onSubmit={submitAwardFeedback} className="space-y-3">
                <StarRating value={feedbackForm.rating} onChange={(n) => setFeedbackForm({ ...feedbackForm, rating: n })} testid="award-feedback-stars" />
                <textarea
                  placeholder="Share your experience (optional)"
                  rows={3}
                  value={feedbackForm.comment}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, comment: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-input bg-background text-sm"
                  data-testid="award-feedback-comment"
                />
                <button type="submit" className="px-4 py-2 rounded bg-navy text-white text-sm font-semibold" data-testid="award-feedback-submit">
                  Submit Feedback
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TenderDetails;
